import type { Estado, Hito, Rol, TipoEvento, TipoVisita, TipoLugar } from './tipos';

const TZ = 'Europe/Madrid';

const fFecha = new Intl.DateTimeFormat('es-ES', {
  timeZone: TZ,
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});
const fFechaLarga = new Intl.DateTimeFormat('es-ES', {
  timeZone: TZ,
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});
const fHora = new Intl.DateTimeFormat('es-ES', { timeZone: TZ, hour: '2-digit', minute: '2-digit' });
const fFechaHora = new Intl.DateTimeFormat('es-ES', {
  timeZone: TZ,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export const fecha = (v: string | Date | null | undefined) => (v ? fFecha.format(new Date(v)) : '—');
export const fechaLarga = (v: string | Date) => fFechaLarga.format(new Date(v));
export const hora = (v: string | Date) => fHora.format(new Date(v));
export const fechaHora = (v: string | Date | null | undefined) =>
  v ? fFechaHora.format(new Date(v)) : '—';

/** «12 sept 2026 · 10:00–13:00» */
export function franja(inicio: string | Date | null, fin: string | Date | null) {
  if (!inicio || !fin) return 'Sin fecha';
  return `${fecha(inicio)} · ${hora(inicio)}–${hora(fin)}`;
}

export function duracion(inicio: string, fin: string) {
  const min = Math.round((new Date(fin).getTime() - new Date(inicio).getTime()) / 60000);
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h && m) return `${h} h ${m} min`;
  if (h) return `${h} h`;
  return `${m} min`;
}

export const ESTADO_ETIQUETA: Record<Estado, string> = {
  PENDING_REVIEW: 'Pendiente de valoración',
  PENDING_FOLLOWUP: 'Pendiente de seguimiento',
  CLOSED: 'Cerrada',
  NOT_PERFORMED: 'No realizada',
  DRAFT: 'Borrador',
  SENT: 'Enviada',
  RECEIVED: 'Recibida',
  AUTHORIZED: 'Autorizada',
  REJECTED: 'Rechazada',
  CANCELLED: 'Cancelada',
};

export const ESTADO_TONO: Record<Estado, 'neutro' | 'espera' | 'hecho' | 'alto'> = {
  PENDING_REVIEW:'espera', PENDING_FOLLOWUP:'espera', CLOSED:'hecho', NOT_PERFORMED:'neutro',
  DRAFT: 'neutro',
  SENT: 'espera',
  RECEIVED: 'espera',
  AUTHORIZED: 'hecho',
  REJECTED: 'alto',
  CANCELLED: 'alto',
};

export const ROL_ETIQUETA: Record<Rol, string> = {
  SALESPERSON: 'Comercial',
  SALES_MANAGER: 'Jefe de Ventas',
  COOK: 'Cocinero',
  ADMIN: 'Administrador',
};

export const TIPO_EVENTO_ETIQUETA: Record<TipoEvento, string> = {
  OWN_VISIT: 'Visita propia',
  VISIT: 'Visita',
  PROPOSED: 'Fecha propuesta',
  UNAVAILABLE: 'Cocina no disponible',
  UNAVAILABILITY: 'Cocina no disponible',
  BLOCKED: 'Bloqueado',
};

export const ACCION_ETIQUETA: Record<string, string> = {
  REVIEW_OPENED:'Valoración abierta por el sistema', EXECUTION_PERFORMED:'Realización confirmada',
  EXECUTION_NOT_PERFORMED:'No realizada definitivamente', EXECUTION_RESCHEDULED:'Reprogramada',
  REVIEW_DRAFT_SAVED:'Borrador de valoración guardado', REVIEW_SUBMITTED:'Valoración enviada',
  REVIEW_EDITED:'Valoración editada', FOLLOWUP_NO:'Seguimiento actualizado',
  FOLLOWUP_SUCCESS:'Cerrada con éxito', FOLLOWUP_AUTO_CLOSED:'Seguimiento cerrado por el sistema',
  MATERIAL_LINES_UPDATED:'Pedido de material actualizado', MATERIAL_ATTACHMENT_ADDED:'Adjunto añadido', MATERIAL_ATTACHMENT_REMOVED:'Adjunto retirado',
  CREATED: 'Solicitud creada',
  UPDATED: 'Datos actualizados',
  SENT: 'Enviada al Jefe de Ventas',
  RECEIVED: 'Recibida por el Jefe de Ventas',
  MANAGER_UPDATED: 'Valoración del Jefe de Ventas',
  MANAGER_BLOCK_UPDATED: 'Valoración del Jefe de Ventas actualizada',
  ADMIN_EDIT_COMMERCIAL_BLOCK: 'Datos del Comercial editados por Administración',
  RESCHEDULED: 'Fecha definitiva reprogramada',
  CREATED_FROM: 'Solicitud creada a partir de otra',
  NEW_PROPOSAL_CREATED: 'Nueva propuesta generada',
  NOT_PERFORMED: 'Visita no realizada',
  AUTHORIZED: 'Visita autorizada',
  REJECTED: 'Solicitud rechazada',
  CANCELLED: 'Visita cancelada',
  SLOT_ADDED: 'Fecha propuesta añadida',
  SLOT_REMOVED: 'Fecha propuesta retirada',
  KITCHEN_PLAN_UPDATED: 'Plan de cocina actualizado',
  KITCHEN_PLAN_CONFIRMED: 'Plan de cocina confirmado',
  KITCHEN_PLAN_RECONFIRMED: 'Plan de cocina reconfirmado',
  KITCHEN_PLAN_REOPENED: 'Plan de cocina reabierto',
  NEW_PROPOSAL: 'Nueva propuesta generada',
  MAIL_QUEUED: 'Correos preparados',
};

export const accion = (a: string) =>
  Object.hasOwn(ACCION_ETIQUETA, a) ? ACCION_ETIQUETA[a]! : 'Movimiento registrado';

/** Fecha en formato aceptado por <input type="datetime-local"> y hora local. */
export function paraInput(v: string | Date | null | undefined) {
  if (!v) return '';
  const d = new Date(v);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}

export const deInput = (v: string) => (v ? new Date(v).toISOString() : null);

export function iso(d: Date) {
  return d.toISOString();
}

export function inicioDia(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function finDia(d: Date) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

export function sumaDias(d: Date, n: number) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

/** Lunes de la semana de `d`. */
export function inicioSemana(d: Date) {
  const x = inicioDia(d);
  const dow = (x.getDay() + 6) % 7;
  return sumaDias(x, -dow);
}

export function inicioMes(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export function mismoDia(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  );
}

export const NOMBRE_MES = new Intl.DateTimeFormat('es-ES', { month: 'long', year: 'numeric' });

/* ────────────────── Autoría de un estado (mejora M-04) ──────────────────
   La cabecera de la ficha NUNCA debe suponer quién ha hecho algo a partir
   del estado. Un Comercial puede cancelar su propia visita, y también el
   Cocinero. El único dato fiable es la última acción del histórico que
   dejó la solicitud en el estado actual, con el rol congelado en el
   momento de actuar.                                                      */

export interface Autoria {
  accion: string;
  etiquetaAccion: string;
  nombre: string;
  rol: Rol | null;
  rolEtiqueta: string;
  fecha: string;
  motivo: string | null;
}

/** Última entrada del histórico que dejó la solicitud en `estado`. */
export function autoriaDe(timeline: Hito[], estado: Estado): Autoria | null {
  let ultimo: Hito | null = null;
  for (const h of timeline) {
    if (h.toStatus === estado) ultimo = h; // el histórico llega en orden ascendente
  }
  if (!ultimo) return null;
  return {
    accion: ultimo.action,
    etiquetaAccion: accion(ultimo.action),
    nombre: ultimo.actorName ?? 'el sistema',
    rol: ultimo.actorRole,
    rolEtiqueta: ultimo.actorRole ? ROL_ETIQUETA[ultimo.actorRole] : 'Sistema',
    fecha: ultimo.createdAt,
    motivo: ultimo.reason,
  };
}

/** Verbo en pasado para la cinta de cierre. */
export const VERBO_ESTADO: Partial<Record<Estado, string>> = {
  AUTHORIZED: 'Autorizada por',
  REJECTED: 'Rechazada por',
  CANCELLED: 'Cancelada por',
  RECEIVED: 'Recibida por',
};

export const TIPO_VISITA_ETIQUETA: Record<TipoVisita, string> = {
  NO_PRODUCT: 'Visita sin producto',
  WITH_PRODUCT: 'Visita con producto',
  CLIENT_AT_HQ: 'Visita del cliente a sede Salvador Pinar',
};
export const TIPO_VISITA_ICONO: Record<TipoVisita, string> = {
  NO_PRODUCT: '💬', WITH_PRODUCT: '🍽', CLIENT_AT_HQ: '🏠',
};
export const tipoVisita = (v?: TipoVisita | null) =>
  v ? `${TIPO_VISITA_ICONO[v]} ${TIPO_VISITA_ETIQUETA[v]}` : 'Sin indicar';
export const lugarVisita = (v?: TipoLugar | null, otro?: string | null, ciudad?: string | null) =>
  v === 'SP_HQ' ? 'Sede Salvador Pinar' : v === 'OTHER' ? otro || 'Otro'
    : v === 'CLIENT' ? `Cliente${ciudad ? ` · ${ciudad}` : ''}` : 'Sin indicar';
